import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Briefcase, Stethoscope, Sparkles, ShoppingBag, Trophy } from 'lucide-react';
import { categories } from '../mock';

const iconMap = {
  Briefcase,
  Stethoscope,
  Sparkles,
  ShoppingBag,
  Trophy
};

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="services" ref={ref} className="relative py-32 bg-gradient-to-b from-[#f5f5f5] to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            服務範疇
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            我們專注於商業空間設計，為不同行業提供量身定制的解決方案
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, index) => {
            const Icon = iconMap[category.icon];
            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 60 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                onHoverStart={() => setHoveredIndex(index)}
                onHoverEnd={() => setHoveredIndex(null)}
                className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/0 to-[#9CAF88]/0 group-hover:from-[#FF6B35]/5 group-hover:to-[#9CAF88]/5 transition-all duration-500" />
                
                <div className="relative p-8">
                  <motion.div
                    animate={{
                      scale: hoveredIndex === index ? 1.1 : 1,
                      rotate: hoveredIndex === index ? 5 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className="w-16 h-16 bg-gradient-to-br from-[#FF6B35] to-[#9CAF88] rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-[#FF6B35] transition-colors duration-300">
                    {category.name}
                  </h3>
                  <p className="text-sm text-[#9CAF88] font-medium mb-4">
                    {category.nameEn}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    {category.description}
                  </p>

                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: hoveredIndex === index ? '100%' : 0 }}
                    transition={{ duration: 0.4 }}
                    className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#FF6B35] to-[#9CAF88]"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
